import Moment from "moment";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./styles.scss";
import BlockIcon from "@mui/icons-material/Block";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import Tooltip from "@mui/material/Tooltip";
import { getUser } from "../../../actions/users";
import { getUserTagsByLegacyIds } from "../../../apis/tags/tag-apis";
import {
  ACTIVE,
  DELETED,
  EXPIRED,
  INACTIVE,
  PAY_AS_YOU_GO,
  SUBSCRIBED,
  UNSUBSCRIBED,
} from "../../../constants/const";
import {
  BLOCK_USER,
  CANCEL_SUBSCRIPTION,
  REMOVE_USER,
  SHOW_COUPON,
  SHOW_LEDGER,
} from "../../../constants/menuActions";
import {
  // USER_PLAN_EXPIRY_DATE,
  USER_ACTIVE_CITY,
  USER_CREATION_DATE,
  USER_EMAIL,
  USER_ID,
  USER_NAME,
  USER_PHONE,
  USER_SUBSCRIPTION_STATUS,
  USER_SUBSCRIPTION_TYPE,
} from "../../../constants/orderConst";
import { getContrastColor } from "../../../domain/tags";
import { strings } from "../../../I18n";
import { useStyles } from "../../../utils/styles";
import {
  sortedItem,
  stringByStatus,
  stringFromValue,
} from "../../../utils/utils";
import DropDownMenu from "../../menu";
import TableHeaderRow from "../../table/tableHeaderRow";
import TableItemRow from "../../table/tableItemRow";
import TabletPaginator from "../../table/tabletPaginator";

function ClientItem(props) {
  const {
    selectMenu,
    clients,
    clientsResult,
    handleChangePage,
    orderProps,
    cities,
    getUser,
    adminDetails,
    tagFilterTotalCount,
    currentPageValue,
  } = props;
  const classes = useStyles();
  const navigate = useNavigate();
  const [userTags, setUserTags] = useState({});

  const effectivePageData = React.useMemo(() => {
    if (tagFilterTotalCount !== null && tagFilterTotalCount !== undefined) {
      return {
        ...clientsResult,
        totalElements: tagFilterTotalCount,
        number: currentPageValue,
      };
    }
    return clientsResult;
  }, [clientsResult, tagFilterTotalCount, currentPageValue]);

  useEffect(() => {
    if (!adminDetails?.newBackendEnabled || !clients?.length) {
      return;
    }

    const legacyIds = clients.map((c) => String(c.id));

    const fetchTags = async () => {
      try {
        const tagsMap = await getUserTagsByLegacyIds(legacyIds);
        const tagsByLegacyId = {};
        for (const [legacyId, tags] of tagsMap.entries()) {
          tagsByLegacyId[legacyId] = tags;
        }
        setUserTags(tagsByLegacyId);
      } catch (e) {
        console.error("Failed to fetch user tags:", e);
      }
    };

    fetchTags();
  }, [clients, adminDetails?.newBackendEnabled]);

  const baseMenuItems = React.useMemo(
    () => [
      {
        action: (client) => selectMenu({ action: REMOVE_USER, client }),
        title: strings("buttons.deleteUser"),
      },
      {
        action: (client) => selectMenu({ action: SHOW_COUPON, client }),
        title: strings("buttons.showCoupons"),
      },
      {
        action: (client) => selectMenu({ action: CANCEL_SUBSCRIPTION, client }),
        title: strings("buttons.cancelSubscription"),
      },
      {
        action: (client) => selectMenu({ action: SHOW_LEDGER, client }),
        title: strings("buttons.showLedger"),
      },
    ],
    [selectMenu]
  );

  const getMenuItems = React.useCallback(
    (client) => {
      if (!adminDetails?.newBackendEnabled) {
        return baseMenuItems;
      }
      const blockItem = {
        action: (c) => selectMenu({ action: BLOCK_USER, client: c }),
        title:
          client?.status === "BLOCKED"
            ? strings("buttons.unblockUser")
            : strings("buttons.blockUser"),
      };
      return [baseMenuItems[0], blockItem, ...baseMenuItems.slice(1)];
    },
    [baseMenuItems, selectMenu, adminDetails?.newBackendEnabled]
  );

  const menuItems = baseMenuItems;

  const clientDropDown = (client) => {
    const items = getMenuItems(client);
    if (client?.plan?.oneTime === true) {
      return (
        <DropDownMenu
          item={client}
          menuItems={items.slice(0, adminDetails?.newBackendEnabled ? 4 : 3)}
          current={strings("descriptions.actions")}
        />
      );
    } else {
      if (
        client?.plan?.type !== PAY_AS_YOU_GO &&
        getSubscriptionStatus(client) === SUBSCRIBED
      ) {
        return (
          <DropDownMenu
            item={client}
            menuItems={items}
            current={strings("descriptions.actions")}
          />
        );
      } else {
        const filteredItems = adminDetails?.newBackendEnabled
          ? [items[0], items[1], items[2], items[4]]
          : [items[0], items[1], items[3]];
        return (
          <DropDownMenu
            item={client}
            menuItems={filteredItems}
            current={strings("descriptions.actions")}
          />
        );
      }
    }
  };

  const renderStatus = React.useCallback((client) => {
    if (client.status === "BLOCKED") {
      return (
        <Chip label="BLOCKED" size="small" color="error" icon={<BlockIcon />} />
      );
    }
    return stringFromValue(client.status);
  }, []);

  const momentDate = (date) =>
    date ? Moment(date + "Z").format("DD.MM.YYYY HH:mm") : "-";

  const getNextBillingDate = (client) => {
    if (client?.plan?.oneTime === true) {
      return "-";
    } else {
      if (client?.plan?.canceledAt === null) {
        return client?.plan?.currentPeriodEnd
          ? Moment(client?.plan?.currentPeriodEnd + "Z").format(
              "DD.MM.YYYY HH:mm"
            )
          : "-";
      } else {
        return "-";
      }
    }
  };

  //Backend requirements logic
  const getPlanExpiryDate = (client) => {
    if (client?.plan?.canceledAt !== null) {
      return client?.plan?.currentPeriodEnd
        ? Moment(client?.plan?.currentPeriodEnd + "Z").format(
            "DD.MM.YYYY HH:mm"
          )
        : "-";
    } else if (client?.plan?.oneTime === true) {
      return client?.plan?.currentPeriodEnd
        ? Moment(client?.plan?.currentPeriodEnd + "Z").format(
            "DD.MM.YYYY HH:mm"
          )
        : "-";
    } else {
      return "-";
    }
  };

  const showClientInfo = async (client) => {
    if (adminDetails?.newBackendEnabled) {
      navigate(
        `/dashboard/customer/${client.id}/${client?.plan?.cityId || client?.activeCityId || 0}`
      );
      return;
    }
    getUser(client.id);
  };

  const clickOnLink = React.useCallback((client) => {
    if (client.stripeProfileUrl) {
      const win = window.open(client.stripeProfileUrl, "_blank");
      if (win != null) {
        win.focus();
      }
    }
  }, []);

  const clientLink = React.useCallback((client) => {
    return client.stripeProfileUrl && client?.stripeProfileUrl !== ""
      ? strings("descriptions.showPayment")
      : "-";
  }, []);

  const link = React.useCallback(
    (client) => (
      <p
        className={"bikeLink"}
        style={client && { color: "blue", cursor: "pointer" }}
        onClick={() => clickOnLink(client)}
      >
        {client?.stripeProfileUrl && clientLink(client)}
      </p>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clients]
  );

  const getPlanStatus = React.useCallback(
    (client) => {
      if (client?.plan?.type === PAY_AS_YOU_GO) {
        if (client?.plan?.status === ACTIVE) {
          return ACTIVE;
        } else if (client?.plan?.status === INACTIVE) {
          return INACTIVE;
        } else if (client?.plan?.status === UNSUBSCRIBED) {
          return "-";
        } else {
          return "-";
        }
      } else {
        if (client?.plan?.status === ACTIVE) {
          return ACTIVE;
        } else if (client?.plan?.status === INACTIVE) {
          return INACTIVE;
        } else {
          return "-";
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clients]
  );

  const getSubscriptionStatus = React.useCallback(
    (client) => {
      if (client?.plan?.type === PAY_AS_YOU_GO) {
        if (
          client?.plan?.status === ACTIVE ||
          client?.plan?.status === INACTIVE
        ) {
          return SUBSCRIBED;
        } else if (client?.plan?.status === UNSUBSCRIBED) {
          return DELETED;
        } else {
          return "-";
        }
      } else {
        if (client?.plan?.currentPeriodEnd === null) {
          return EXPIRED;
        } else if (client?.plan?.canceledAt === null) {
          return SUBSCRIBED;
        } else if (client?.plan?.canceledAt !== null) {
          return UNSUBSCRIBED;
        } else {
          return "-";
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clients]
  );

  const titles = React.useMemo(
    () => [
      "",
      strings("descriptions.userId"),
      strings("descriptions.name"),
      strings("descriptions.email"),
      strings("descriptions.phone"),
      strings("descriptions.cityName"),
      "Tags",
      strings("descriptions.accountStatus"),
      strings("descriptions.creationDate"),
      strings("descriptions.emailVerified"),
      strings("descriptions.subscription"),
      strings("descriptions.planType"),
      strings("descriptions.planStatus"),
      strings("descriptions.planExpiryDate"),
      strings("descriptions.nextBillingDate"),
      strings("descriptions.link"),
      "",
    ],
    []
  );

  const renderTags = React.useCallback(
    (clientId) => {
      const tags = userTags[clientId] || [];
      if (tags.length === 0) {
        return <span style={{ color: "#999" }}>-</span>;
      }
      return (
        <Box
          sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", maxWidth: 150 }}
        >
          {tags.slice(0, 3).map((ut) => (
            <Tooltip key={ut.id} title={ut.tag.name}>
              <Chip
                label={ut.tag.name}
                size="small"
                sx={{
                  backgroundColor: ut.tag.color,
                  color: getContrastColor(ut.tag.color),
                  height: 20,
                  fontSize: "0.7rem",
                  maxWidth: 70,
                  "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            </Tooltip>
          ))}
          {tags.length > 3 && (
            <Chip
              label={`+${tags.length - 3}`}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: "0.7rem" }}
            />
          )}
        </Box>
      );
    },
    [userTags]
  );

  const edit = React.useCallback(
    (user) => (
      <div className={"edit"} onClick={() => showClientInfo(user)}>
        <div className={"userLink"} style={{ padding: 5 }}>
          {strings("descriptions.show")}
        </div>
      </div>
    ),
    [adminDetails, navigate, getUser]
  );

  const sortedFields = React.useMemo(
    () => [
      sortedItem(1, USER_ID, orderProps),
      sortedItem(2, USER_NAME, orderProps),
      sortedItem(3, USER_EMAIL, orderProps),
      sortedItem(4, USER_PHONE, orderProps),
      sortedItem(5, USER_ACTIVE_CITY, orderProps),
      sortedItem(6, USER_CREATION_DATE, orderProps),
      sortedItem(8, USER_SUBSCRIPTION_STATUS, orderProps),
      sortedItem(9, USER_SUBSCRIPTION_TYPE, orderProps),
      // sortedItem(10, USER_PLAN_EXPIRY_DATE, orderProps),
    ],
    [orderProps]
  );

  const getPricingPlanType = (baseType, isWallet) => {
    if (isWallet) {
      return strings("descriptions.wallet");
    } else if (baseType) {
      return baseType.replace(/_/g, " ");
    }
    return "";
  };

  const tabletBody = () => (
    <TableBody>
      {clients?.map((client) => (
        <TableItemRow
          id={client.id}
          key={`${client.id}-${client?.plan?.cityId || 0}`}
          items={[
            edit(client),
            stringFromValue(client.id),
            stringFromValue(client.fullName),
            stringFromValue(client.email),
            stringFromValue(client.phone),
            stringFromValue(
              cities.find((city) => city.id === client?.plan?.cityId)?.name
            ),
            renderTags(client.id),
            renderStatus(client),
            momentDate(client?.dateCreated),
            stringByStatus(client.emailVerified),
            getSubscriptionStatus(client),
            getPricingPlanType(
              client?.plan?.type,
              client?.plan?.paymentMethodWallet
            ),
            getPlanStatus(client),
            getPlanExpiryDate(client),
            getNextBillingDate(client),
            link(client),
            clientDropDown(client),
          ]}
        />
      ))}
    </TableBody>
  );

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <TableContainer>
          <Table className={classes.table} aria-label="simple table">
            <TableHeaderRow items={titles} sortedIndexes={sortedFields} />
            {tabletBody()}
          </Table>
        </TableContainer>
        <TabletPaginator
          items={clients}
          pageData={effectivePageData}
          handleChangePage={handleChangePage}
        />
      </Paper>
    </div>
  );
}

const mapStateToProps = ({ auth: { adminDetails } }) => ({
  adminDetails,
});

const mapDispatchToProps = {
  getUser,
};

export default connect(mapStateToProps, mapDispatchToProps)(ClientItem);
